import type { AxiosInstance } from "axios";
import { Loader2, Store, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";

import { PublicPage } from "../components/PublicPage.js";
import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { apiErrorMessage } from "../lib/api.js";
import { fetchPublicInvite, formatInviteDate, startInvitedInstall, type PublicInvite } from "../lib/store-invites.js";
import { normaliseShop } from "./AddStoreScreen.js";

/**
 * Where an agency's client lands from the invite link (`/install/invite?token=…`) — NO session: the
 * client has no Ecomally account, and needs none. They name their Shopify store and are sent to
 * Shopify to install; the gateway checks the invite again and the store lands in the agency.
 *
 * A link that no longer works says so and stops — it never offers a plain install, which would give
 * the client a separate account of their own: the one thing the agency sent the link to avoid.
 */
const DEAD: Record<"used" | "expired" | "revoked", string> = {
  used: "This invite link has already been used to connect a store.",
  expired: "This invite link has expired.",
  revoked: "This invite link was withdrawn by the agency.",
};

export function InviteInstallScreen({ client, token }: { readonly client: AxiosInstance; readonly token: string }): React.JSX.Element {
  const [invite, setInvite] = useState<PublicInvite | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [shop, setShop] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchPublicInvite(client, token)
      .then(setInvite)
      .catch((caught: unknown) => setLoadError(apiErrorMessage(caught, "This invite link is not valid.")));
  }, [client, token]);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Full-page navigation to the gateway, then Shopify's consent screen — not a fetch.
      window.location.assign(await startInvitedInstall(client, token, normaliseShop(shop)));
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not start the install. Please try again."));
      setBusy(false);
    }
  };

  if (loadError !== null) {
    return (
      <PublicPage>
        <Dead message={loadError} />
      </PublicPage>
    );
  }
  if (invite === null) {
    return (
      <PublicPage>
        <p className="text-muted-foreground flex items-center justify-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" /> Checking your invite…
        </p>
      </PublicPage>
    );
  }
  if (invite.status !== "active") {
    return (
      <PublicPage>
        <Dead message={DEAD[invite.status]} agency={invite.agencyName} />
      </PublicPage>
    );
  }

  return (
    <PublicPage wide>
      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <span className="bg-primary/10 text-primary flex size-11 items-center justify-center rounded-full">
            <Store className="size-5" />
          </span>
          <h1 className="text-xl font-semibold">Connect your store to {invite.agencyName}</h1>
          <p className="text-muted-foreground text-sm">
            {invite.agencyName} uses Ecomally to report on your marketing. Install the Ecomally app on your Shopify store and
            it joins their account as <span className="text-foreground font-medium">{invite.clientName}</span> — you do not
            need an Ecomally account of your own.
          </p>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="invite-shop">Your Shopify store</Label>
          <Input
            id="invite-shop"
            required
            autoFocus
            placeholder="your-store.myshopify.com"
            value={shop}
            onChange={(event) => setShop(event.target.value)}
            disabled={busy}
          />
          <p className="text-muted-foreground text-xs">
            The myshopify.com address, found in Shopify under Settings → Domains. This link works once, until{" "}
            {formatInviteDate(invite.expiresAt)}.
          </p>
        </div>

        {error !== null && (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}

        <Button type="submit" disabled={busy || shop.trim() === ""}>
          {busy && <Loader2 className="animate-spin" />}
          Continue to Shopify
        </Button>
      </form>
    </PublicPage>
  );
}

function Dead({ message, agency }: { readonly message: string; readonly agency?: string }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <span className="flex size-12 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300">
        <TriangleAlert className="size-6" />
      </span>
      <h1 className="text-lg font-semibold">This link cannot be used</h1>
      <p className="text-muted-foreground text-sm">
        {message} Ask {agency ?? "your agency"} to send you a new one.
      </p>
    </div>
  );
}
